/**
 * Currency Settings Component
 * 
 * ตั้งค่าสกุลเงินและการแสดงผลตามภาษา/ประเทศ
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Coins,
  Eye,
  Calculator,
  Check,
  Globe,
} from 'lucide-react'
import { useCurrency, ENABLED_CURRENCIES, type CurrencyConfig } from '@/stores/currency-store'
import { FlagIcon } from '@/components/ui/flag-icon'
import { cn } from '@/lib/utils'

export function CurrencySettings() {
  const {
    currency,
    currencyCode,
    showBothValues,
    primaryDisplay,
    setCurrency,
    setShowBothValues,
    setPrimaryDisplay,
    format,
    formatCredit,
    formatLocal,
  } = useCurrency()

  // Preview amount
  const previewCredits = [100, 1000, 10000, 50000]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <Globe className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h3 className="font-semibold">ตั้งค่าสกุลเงิน</h3>
          <p className="text-sm text-muted-foreground">
            เลือกประเทศ/ภาษาสำหรับการแสดงเงินในระบบ
          </p>
        </div>
      </div>

      {/* Country/Language Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />
            เลือกประเทศ
          </CardTitle>
          <CardDescription>
            1 เครดิต จะถูกแปลงเป็นสกุลเงินของประเทศที่เลือก
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {ENABLED_CURRENCIES.map((curr) => (
              <CurrencyCard
                key={curr.code}
                currency={curr}
                isSelected={currencyCode === curr.code}
                onSelect={() => setCurrency(curr.code)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Display Settings - Show only if not THB */}
      {currency.multiplier !== 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary" />
              การแสดงผล
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Show Both Values */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="space-y-0.5">
                <Label>แสดงทั้งเครดิตและสกุลเงินท้องถิ่น</Label>
                <p className="text-xs text-muted-foreground">
                  เช่น 1,000.00 ({(1000 * currency.multiplier).toLocaleString()} {currency.symbol})
                </p>
              </div>
              <Switch
                checked={showBothValues}
                onCheckedChange={setShowBothValues}
              />
            </div>

            {/* Primary Display */}
            {showBothValues && (
              <div className="space-y-2">
                <Label>แสดงค่าหลักเป็น</Label>
                <Select value={primaryDisplay} onValueChange={(v) => setPrimaryDisplay(v as 'credit' | 'local')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">
                      <span className="flex items-center gap-2">
                        <Coins className="w-4 h-4" />
                        เครดิต (ค่าหลักในระบบ)
                      </span>
                    </SelectItem>
                    <SelectItem value="local">
                      <span className="flex items-center gap-2">
                        <FlagIcon country={currency.flagCode as any} size="sm" />
                        {currency.nameTh} ({currency.symbol})
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* THB Info Box */}
      {currency.multiplier === 1 && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
          <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-emerald-900 dark:text-emerald-100">
              ประเทศไทย - อัตราแลกเปลี่ยน 1:1
            </p>
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
              1 เครดิต = 1 บาท ไม่ต้องแปลงค่า
            </p>
          </div>
        </div>
      )}

      {/* Preview - Show only if not THB */}
      {currency.multiplier !== 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="w-4 h-4 text-primary" />
              ตัวอย่างการแสดงผล
            </CardTitle>
            <CardDescription>
              ดูว่าค่าเครดิตจะแสดงผลอย่างไรในระบบ
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium">เครดิต</th>
                    <th className="text-left py-2 px-3 font-medium">
                      <div className="flex items-center gap-2">
                        <FlagIcon country={currency.flagCode as any} size="sm" />
                        {currency.nameTh}
                      </div>
                    </th>
                    <th className="text-left py-2 px-3 font-medium">แสดงในระบบ</th>
                  </tr>
                </thead>
                <tbody>
                  {previewCredits.map((credit) => (
                    <tr key={credit} className="border-b last:border-0">
                      <td className="py-2 px-3 font-mono">{formatCredit(credit)}</td>
                      <td className="py-2 px-3 font-mono text-muted-foreground">{formatLocal(credit)}</td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="font-mono">
                          {format(credit)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Formula */}
            <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <p className="font-medium text-amber-900 dark:text-amber-100 mb-1 text-sm">
                อัตราแลกเปลี่ยน:
              </p>
              <code className="text-sm bg-background px-2 py-1 rounded font-mono">
                1 เครดิต = {currency.multiplier.toLocaleString()} {currency.symbol}
              </code>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ============================================
// Currency Selection Card
// ============================================

function CurrencyCard({ 
  currency, 
  isSelected, 
  onSelect 
}: { 
  currency: CurrencyConfig
  isSelected: boolean
  onSelect: () => void 
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "relative p-4 rounded-xl border-2 text-left transition-all",
        "hover:shadow-md",
        isSelected 
          ? "border-primary bg-primary/5 shadow-md" 
          : "border-border hover:border-primary/50 hover:bg-muted/50"
      )}
    >
      {/* Selected Indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2">
          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
            <Check className="w-3 h-3 text-primary-foreground" />
          </div>
        </div>
      )}
      
      {/* Flag & Country Name */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
          <FlagIcon country={currency.flagCode as any} size="md" />
        </div>
        <div>
          <p className="font-semibold">{currency.nameTh}</p>
          <p className="text-xs text-muted-foreground">{currency.nameLocal}</p>
        </div>
      </div>
      
      {/* Exchange Rate */}
      <div className="pt-3 border-t">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">อัตราแลกเปลี่ยน</span>
          <Badge 
            variant={currency.multiplier === 1 ? "default" : "secondary"}
            className={cn(
              "font-mono text-xs",
              currency.multiplier === 1 && "bg-emerald-500"
            )}
          >
            1:{currency.multiplier.toLocaleString()}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          1 เครดิต = {currency.multiplier.toLocaleString()} {currency.symbol}
        </p>
      </div>
    </button>
  )
}
